import { Preferences } from '@capacitor/preferences'
import type { RouteLocationNormalized } from 'vue-router'

import { USER_ROLES } from '@/constants/constants'
import type { FeatureResponse } from '@/generated/api'
import { ApiError } from '@/generated/api'
import { APPOINTMENTS } from '@/modules/appointments/routes'
import { MEAL, MEAL_DATE, MEAL_NAME } from '@/modules/meal/routes'

import { ABSENCE, STATION_BOARD } from '../absence/routes'
import { HOME } from '../home/routes'
import { INFO, INFO_DETAILS } from '../info/routes'
import { NEWS } from '../news/routes'
import { MESSAGING } from '../patient-messaging/routes'
import { PATIENTS } from '../patients/routes'
import { BSCL_DETAILS, SURVEY_DETAILS, SURVEYS } from '../survey/routes'
import { LOGIN, PASSWORD_RESET, SET_NEW_PASSWORD } from './routes'
import { useAuthStore } from './store'

const featureRoutesRelations: Record<string, keyof FeatureResponse> = {
  [APPOINTMENTS]: 'appointments',
  [NEWS]: 'news',
  [INFO]: 'knowledge_base',
  [INFO_DETAILS]: 'knowledge_base',
  [SURVEYS]: 'questionnaires',
  [SURVEY_DETAILS]: 'questionnaires',
  [BSCL_DETAILS]: 'questionnaires',
  [ABSENCE]: 'absence_module',
  [MEAL]: 'menu_planner',
  [MEAL_DATE]: 'menu_planner',
  [MEAL_NAME]: 'menu_planner',
}

export const authMiddleware = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => {
  const loginRoute = {
    name: LOGIN,
    query: { redirect_to: to.fullPath !== '/' ? to.fullPath : undefined },
  }

  const store = useAuthStore()
  const stationBoard = await Preferences.get({ key: 'stationBoard' }).then((res) => res.value)

  // Check stationBoard preference with safe JSON parsing
  if (
    to.name !== STATION_BOARD &&
    to.name !== PASSWORD_RESET &&
    to.name !== SET_NEW_PASSWORD &&
    stationBoard
  ) {
    try {
      const isStationBoardMode = JSON.parse(stationBoard)
      if (isStationBoardMode) {
        return { name: STATION_BOARD }
      }
    } catch (error) {
      // Invalid JSON in stationBoard preference, clear it
      console.warn('[Auth] Invalid stationBoard preference, clearing:', error)
      await Preferences.remove({ key: 'stationBoard' })
    }
  }

  try {
    const user = await store.fetchMe()
    if (user && to.name === LOGIN) {
      return to.query.redirect_to ? { path: to.query.redirect_to } : { path: from.path }
    }
    if (
      user &&
      user.type === USER_ROLES.PRACTITIONER &&
      [APPOINTMENTS, SURVEYS, HOME, ABSENCE].includes(to.name as string)
    ) {
      return { name: PATIENTS }
    }

    if (
      (user &&
        user.type === USER_ROLES.PATIENT &&
        [PATIENTS, MESSAGING].includes(to.name as string)) ||
      (featureRoutesRelations[to.name as string] &&
        !(await store.getFeatures())[featureRoutesRelations[to.name as string]])
    ) {
      return user ? { name: HOME } : { name: LOGIN, query: { redirect_to: to.fullPath } }
    }

    if (!to.meta.auth) {
      return true
    }
    if (!user) {
      await store.logout(true)
      return loginRoute
    }
  } catch (e) {
    if (e instanceof ApiError) {
      if (e?.status >= 500) {
        return '/500'
      }
      if ([401].includes(e?.status)) {
        await store.logout(true)
        return loginRoute
      }
    }
    throw e
  }
  return true
}
